import { z } from 'zod';
import {
    minorUnitsFromAmount,
    normalizeAmountString,
    splitCsv,
    type MoneyJson,
} from '@/shared/components/form/config-value';

export const CONFIG_DATA_TYPES = ['string', 'integer', 'decimal', 'boolean', 'money', 'multiselect'] as const;
export type ConfigDataType = (typeof CONFIG_DATA_TYPES)[number];

const applyValueTypeChecks = (v: { data_type: ConfigDataType; raw_value: string }, ctx: z.RefinementCtx) => {
    if (v.data_type === 'boolean') {
        if (v.raw_value !== 'true' && v.raw_value !== 'false') {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['raw_value'], message: 'Choose Yes or No' });
        }
        return;
    }
    if (v.data_type === 'multiselect') {
        if (splitCsv(v.raw_value).length === 0) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['raw_value'], message: 'Select at least one option' });
        }
        return;
    }
    if (v.raw_value.trim() === '') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['raw_value'], message: 'Value is required' });
        return;
    }
    if (v.data_type === 'integer') {
        if (!/^-?\d+$/.test(v.raw_value.trim())) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['raw_value'], message: 'Enter a whole number' });
        }
        return;
    }
    if (v.data_type === 'decimal' || v.data_type === 'money') {
        if (normalizeAmountString(v.raw_value) === null) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['raw_value'], message: 'Enter a numeric amount' });
        }
    }
};

const transformValue = (v: { data_type: ConfigDataType; raw_value: string }) => {
    if (v.data_type === 'boolean') return v.raw_value === 'true';
    if (v.data_type === 'multiselect') return splitCsv(v.raw_value);
    if (v.data_type === 'integer') return parseInt(v.raw_value, 10);
    if (v.data_type === 'decimal') return parseFloat(v.raw_value);
    if (v.data_type === 'money') {
        const amount = normalizeAmountString(v.raw_value) ?? v.raw_value;
        const minor = minorUnitsFromAmount(amount);
        const money: MoneyJson = {
            amount,
            currency: 'GBP',
            // Decimal-safe string arithmetic, never parseFloat * 100; backend Brick\Money stays canonical.
            minor: minor ?? 0,
        };
        return money;
    }
    return v.raw_value;
};

export function makeConfigSettingAddSchema(opts: { keySchema: z.ZodType<string> }) {
    return z
        .object({
            config_option_id: z.string().uuid().optional(),
            key: opts.keySchema,
            data_type: z.enum(CONFIG_DATA_TYPES),
            raw_value: z.string(),
        })
        .superRefine(applyValueTypeChecks)
        .transform((v) => ({ ...v, value: transformValue(v) }));
}

export type ConfigSettingAddSchema = ReturnType<typeof makeConfigSettingAddSchema>;
export type ConfigSettingAddInput = z.input<ConfigSettingAddSchema>;
export type ConfigSettingAddOutput = z.output<ConfigSettingAddSchema>;
